<template>
  <div class="contest">
    <div class="container contest__container">
      <!-- Контент конкурса -->
      <template v-if="exhibition?.is_contest">
        <div class="contest__header">
          <h1 class="contest__title">
            {{ $t("contest.title") }}
          </h1>
        </div>

        <div class="contest__gallery">
          <img
            src="/static/images/contest/image1.png"
            alt="Metobr 2026"
            class="contest__gallery-image"
          />
        </div>

        <div class="contest__intro">
          <p class="contest__greeting">
            {{ $t("contest.greeting") }}
          </p>
          <p>
            {{ $t("contest.intro.paragraph1") }}
          </p>
          <p>
            {{ $t("contest.intro.paragraph2") }}
          </p>
          <p class="contest__highlight">
            {{ $t("contest.intro.highlight") }}
          </p>
        </div>

        <div class="contest__section">
          <h2 class="contest__section-title">
            {{ $t("contest.howItWorks.title") }}
          </h2>
          <div class="contest__content">
            <p>
              {{ $t("contest.howItWorks.content") }}
            </p>
          </div>
        </div>

        <div class="contest__prizes">
          <h2 class="contest__section-title">
            {{ $t("contest.prizes.title") }}
          </h2>
          <div class="contest__prizes-grid">
            <div class="prize-card prize-card_first">
              <div class="prize-card__badge">
                {{ $t("contest.prizes.first.badge") }}
              </div>
              <div class="prize-card__content">
                <h3 class="prize-card__title">
                  {{ $t("contest.prizes.first.title") }}
                </h3>
                <ul class="prize-card__list">
                  <li
                    v-for="(item, index) in $t('contest.prizes.first.items')"
                    :key="index"
                  >
                    {{ item }}
                  </li>
                </ul>
                <p class="prize-card__subtitle">
                  {{ $t("contest.prizes.first.subtitle") }}
                </p>
              </div>
            </div>

            <div class="prize-card prize-card_second">
              <div class="prize-card__badge">
                {{ $t("contest.prizes.second.badge") }}
              </div>
              <div class="prize-card__content">
                <h3 class="prize-card__title">
                  {{ $t("contest.prizes.second.title") }}
                </h3>
                <ul class="prize-card__list">
                  <li
                    v-for="(item, index) in $t('contest.prizes.second.items')"
                    :key="index"
                  >
                    {{ item }}
                  </li>
                </ul>
                <p class="prize-card__subtitle">
                  {{ $t("contest.prizes.second.subtitle") }}
                </p>
              </div>
            </div>

            <div class="prize-card prize-card_third">
              <div class="prize-card__badge">
                {{ $t("contest.prizes.third.badge") }}
              </div>
              <div class="prize-card__content">
                <h3 class="prize-card__title">
                  {{ $t("contest.prizes.third.title") }}
                </h3>
                <ul class="prize-card__list">
                  <li
                    v-for="(item, index) in $t('contest.prizes.third.items')"
                    :key="index"
                  >
                    {{ item }}
                  </li>
                </ul>
                <p class="prize-card__subtitle">
                  {{ $t("contest.prizes.third.subtitle") }}
                </p>
              </div>
            </div>
          </div>
          <p class="contest__section-subtitle">
            {{ $t("contest.prizes.subtitle") }}
          </p>
        </div>
        <div class="contest__closing">
          <p>
            {{ $t("contest.closing.paragraph1") }}
          </p>

          <p v-for="item in banners" :key="item.id">
            <a
              :href="item.imageLink"
              :download="item.imageName"
              class="contest__banner-link"
            >
              {{ $t("contest.closing.downloadBanner") }}
              ({{ item.aspectRatio }})
            </a>
          </p>

          <p class="contest__highlight">
            {{ $t("contest.closing.highlight") }}
          </p>
        </div>

        <div class="contest__info-grid">
          <div class="contest__info-card">
            <div class="contest__info-label">
              {{ $t("contest.info.datesLabel") }}
            </div>
            <div class="contest__info-value">
              {{ $t("contest.info.datesValue") }}
            </div>
          </div>

          <div class="contest__info-card">
            <div class="contest__info-label">
              {{ $t("contest.info.resultsLabel") }}
            </div>
            <div class="contest__info-value">
              {{ $t("contest.info.resultsValue") }}
            </div>
          </div>
        </div>

        <div class="contest__footer">
          <p class="contest__motto">
            {{ $t("contest.motto") }}
          </p>
        </div>

        <div v-if="!currentUser?.is_agree_contest" class="contest__agreement">
          <Checkbox v-model="agreementChecked" class="contest__checkbox">
            {{ $t("contest.agreement.termsPrefix") }}
            <a
              :href="$t('contest.agreement.termsUrl')"
              target="_blank"
              rel="noopener noreferrer"
              >{{ $t("contest.agreement.termsLink") }}</a
            >{{ $t("contest.agreement.termsSuffix") }}
          </Checkbox>

          <Checkbox v-model="rulesChecked" class="contest__checkbox">
            {{ $t("contest.agreement.rules") }}
          </Checkbox>
        </div>

        <div class="contest__action">
          <button
            class="contest__button"
            :disabled="!canParticipate || isLoading"
            @click="handleParticipate"
          >
            {{
              isLoading
                ? $t("contest.button.loading")
                : currentUser?.is_agree_contest
                ? $t("contest.button.getLink")
                : $t("contest.button.participate")
            }}
          </button>
        </div>

        <img
          class="contest__banner"
          src="/static/images/contest/footer-banner.jpg"
          alt="Contest"
        />
      </template>

      <!-- Сообщение когда конкурсов нет -->
      <div v-else class="contest__no-contest">
        <h2 class="contest__no-contest-title">
          {{ $t("contest.noContest.title") }}
        </h2>
        <p class="contest__no-contest-text">
          {{ $t("contest.noContest.text") }}
        </p>
        <PrimaryButton class="contest__no-contest-button" @click="goBack">
          {{ $t("contest.noContest.button") }}
        </PrimaryButton>
      </div>
    </div>

    <Popup
      v-if="showCopyPopup"
      with-in-modal
      @close="showCopyPopup = false"
      class="m-0"
    >
      <div class="success-message">
        <div class="success-message__link-container contest__info-card">
          <button @click="copyToClipboard(url)">
            <icon name="copy" width="16" height="16" />
          </button>
          <span>
            {{ this.url }}
          </span>
        </div>

        <p class="success-message__text">
          {{ $t("contest.successPopup.text") }}
        </p>
        <ul class="success-message__list">
          <li class="success-message__item">
            <span class="success-message__bullet">→</span>
            {{ $t("contest.successPopup.item1") }}
          </li>
          <li class="success-message__item">
            <span class="success-message__bullet">→</span>
            {{ $t("contest.successPopup.item2") }}
          </li>
        </ul>

        <button class="primary-button" @click="copyToClipboard(url)">
          {{ $t("contest.successPopup.copy") }}
        </button>
      </div>

      <Popup
        v-if="showSuccessPopup"
        message
        @close="showSuccessPopup = false"
        class="m-0"
      >
        <p class="success-message__title">
          {{ $t("contest.successPopup.title") }}
        </p>
      </Popup>
    </Popup>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { Checkbox, Popup, PrimaryButton } from "@v2/shared/ui/components";

export default {
  name: "ContestPage",

  components: {
    Checkbox,
    Popup,
    PrimaryButton
  },

  data() {
    return {
      agreementChecked: false,
      rulesChecked: false,
      isLoading: false,
      showCopyPopup: false,
      showSuccessPopup: false,
      url: "",

      banners: [
        {
          id: 1,
          imageLink:
            "/static/images/contest/Metobr_2026_banners_stands_240x400.jpg",
          imageName: "Metobr_2026_banners_stands_240x400.jpg",
          aspectRatio: "240x400"
        },
        {
          id: 2,
          imageLink:
            "/static/images/contest/Metobr_2026_banners_stands_320x100.jpg",
          imageName: "Metobr_2026_banners_stands_320x100.jpg",
          aspectRatio: "320x100"
        },
        {
          id: 3,
          imageLink:
            "/static/images/contest/Metobr_2026_banners_stands_400x200.jpg",
          imageName: "Metobr_2026_banners_stands_400x200.jpg",
          aspectRatio: "400x200"
        },
        {
          id: 4,
          imageLink:
            "/static/images/contest/Metobr_2026_banners_stands_600x300.jpg",
          imageName: "Metobr_2026_banners_stands_600x300.jpg",
          aspectRatio: "600x300"
        }
      ]
    };
  },

  computed: {
    ...mapGetters("event", ["currentUser", "exhibition"]),

    canParticipate() {
      if (this.currentUser?.is_agree_contest) {
        return true;
      }
      return this.agreementChecked && this.rulesChecked;
    }
  },

  methods: {
    ...mapActions("event", ["setAgreeContests", "getEvent"]),

    goBack() {
      const participantId = this.$route.params.id;
      this.$router.push(`/participant/${participantId}`);
    },

    async handleParticipate() {
      if (!this.canParticipate || this.isLoading) {
        return;
      }

      try {
        this.isLoading = true;
        const response = await this.setAgreeContests({
          is_agree_contest: true
        });

        this.url = response?.data?.url || response?.url;

        if (this.url) {
          // Открываем popup с копированием ссылки
          this.showCopyPopup = true;
        }

        // Обновляем данные пользователя только если это первое согласие
        if (!this.currentUser?.is_agree_contest) {
          await this.getEvent(this.$route.params.id);
        }
      } catch (error) {
        console.error("Error in handleParticipate:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async copyToClipboard(text) {
      if (!text) {
        return false;
      }

      try {
        // Пробуем современный API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          this.showSuccessPopup = true;
          return true;
        }

        // Fallback для старых браузеров
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        const successful = document.execCommand("copy");
        document.body.removeChild(textarea);

        this.showSuccessPopup = true;
        return successful;
      } catch (error) {
        return false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.contest {
  background: #f9f9f9;
  padding: 50px 0 56px 0;
  flex-grow: 1;

  @include respond-down(md) {
    padding: 30px 0 40px 0;
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: 64px;
  }

  &__header {
    text-align: center;
  }

  &__title {
    @include h1();
    margin: 0;
    color: #262626;
  }

  &__gallery {
    display: none;

    @include respond-up(lg) {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      gap: 20px;
    }
  }

  &__gallery-image {
    width: 100%;
    object-fit: contain;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: #f5f5f5;

    @include respond-down(md) {
      height: 180px;
    }
  }

  &__intro {
    display: flex;
    flex-direction: column;
    gap: 16px;

    @include respond-down(md) {
      gap: 12px;
    }
    p {
      font-size: 15px;
      line-height: 1.7;
      color: #262626;
      margin: 0;

      @include respond-down(md) {
        font-size: 14px;
      }
    }
  }

  &__greeting {
    font-weight: 600;
    color: #005197;
    font-size: 16px;

    @include respond-down(md) {
      font-size: 15px;
    }
  }

  &__highlight {
    font-weight: 600;
    color: #009ee3;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 24px;

    @include respond-down(md) {
      gap: 16px;
    }
  }

  &__section-title {
    @include h2();
    margin: 0;
  }

  &__section-subtitle {
    margin: 0;
    text-align: center;
  }

  &__content {
    p {
      font-size: 15px;
      line-height: 1.7;
      color: #262626;
      margin: 0;

      @include respond-down(md) {
        font-size: 14px;
      }
    }
  }

  &__prizes {
    display: flex;
    flex-direction: column;
    gap: 24px;

    @include respond-down(md) {
      gap: 16px;
    }
  }

  &__prizes-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;

    @include respond-down(lg) {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }

  &__closing {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include respond-down(md) {
      gap: 12px;
    }

    p {
      font-size: 15px;
      line-height: 1.7;
      color: #262626;
      margin: 0;
      @include respond-down(md) {
        font-size: 14px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__banner-link {
    color: #009ee3;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
      color: #005197;
    }
  }

  &__info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;

    @include respond-down(lg) {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }

  &__info-card {
    background: #ffffff;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    gap: 8px;
    border: 1px solid #e0e0e0;

    @include respond-down(md) {
      padding: 20px 16px;
    }
  }

  &__info-label {
    font-size: 13px;
    color: #6d7179;
    font-weight: 400;

    @include respond-down(md) {
      font-size: 11px;
    }
  }

  &__info-value {
    font-size: 18px;
    color: #262626;
    font-weight: 500;

    @include respond-down(md) {
      font-size: 16px;
    }
  }

  &__footer {
    text-align: center;

    p {
      font-size: 14px;
      line-height: 1.6;
      color: #262626;
      margin: 0 0 12px 0;

      @include respond-down(md) {
        font-size: 13px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__motto {
    font-weight: 600;
    color: #005197;
    font-size: 15px;

    @include respond-down(md) {
      font-size: 14px;
    }
  }

  &__agreement {
    max-width: 800px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @include respond-down(md) {
      gap: 12px;
    }
  }

  &__checkbox {
    display: flex;
    align-items: flex-start;

    ::v-deep .checkbox__text {
      font-size: 14px;
      line-height: 1.6;

      @include respond-down(md) {
        font-size: 13px;
      }

      a {
        color: #009ee3;
        text-decoration: underline;
        transition: color 0.2s ease;

        &:hover {
          color: #005197;
        }
      }
    }
  }

  &__action,
  &__banner {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__button {
    background: linear-gradient(135deg, #005197 0%, #009ee3 100%);
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    padding: 20px 60px;
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 81, 151, 0.3);
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @include respond-down(md) {
      font-size: 16px;
      padding: 16px 40px;
      width: 100%;
    }

    &:not(:disabled):hover {
      box-shadow: 0 6px 16px rgba(0, 81, 151, 0.4);
      transform: translateY(-2px);
    }

    &:not(:disabled):active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(0, 81, 151, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      background: #9e9e9e;
      box-shadow: none;
    }
  }

  &__no-contest {
    background: #ffffff;
    padding: 80px 40px;
    text-align: center;
    border: 1px solid #e0e0e0;
    margin: 40px 0;

    @include respond-down(md) {
      padding: 60px 24px;
      margin: 32px 0;
    }
  }

  &__no-contest-icon {
    font-size: 80px;
    margin-bottom: 24px;
    opacity: 0.3;

    @include respond-down(md) {
      font-size: 60px;
      margin-bottom: 20px;
    }
  }

  &__no-contest-title {
    @include h2();
    color: #262626;
    margin: 0;
    margin: 0 0 16px 0;

    @include respond-down(md) {
      font-size: 20px;
      margin: 0 0 12px 0;
    }
  }

  &__no-contest-text {
    font-size: 16px;
    line-height: 1.6;
    color: #6d7179;
    margin: 0 0 32px 0;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;

    @include respond-down(md) {
      font-size: 14px;
      margin-bottom: 24px;
    }
  }

  &__no-contest-button {
    display: block;
    margin: 0 auto;
    width: auto;

    @include respond-down(md) {
      width: 100%;
      max-width: 300px;
    }
  }
}

.prize-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
  padding: 24px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;

  @include respond-down(md) {
    padding: 20px 16px;
  }

  &_first {
    border-color: #ffd700;

    .prize-card__badge {
      background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
      color: #8b7500;
    }
  }

  &_second {
    border-color: #c0c0c0;

    .prize-card__badge {
      background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
      color: #5a5a5a;
    }
  }

  &_third {
    border-color: #cd7f32;

    .prize-card__badge {
      background: linear-gradient(135deg, #cd7f32 0%, #e89e5e 100%);
      color: #6b4119;
    }
  }

  &__badge {
    display: inline-block;
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 600;
    max-width: 80px;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @include respond-down(md) {
      font-size: 11px;
      padding: 5px 12px;
    }
  }

  &__content {
    display: flex;
    height: 100%;
    flex-direction: column;
    gap: 12px;
  }

  &__title {
    @include h3();
    font-size: 18px;
    margin: 0;

    @include respond-down(md) {
      font-size: 16px;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      font-size: 13px;
      line-height: 1.6;
      color: #505050;
      padding-left: 20px;
      position: relative;

      @include respond-down(md) {
        font-size: 12px;
      }

      &::before {
        content: "✓";
        position: absolute;
        left: 0;
        color: #009ee3;
        font-weight: bold;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__subtitle {
    display: flex;
    justify-self: end;
    margin-top: auto;
    margin-bottom: 0;
    padding-top: 3em;
    font-size: 13px;
    line-height: 1.6;
    color: #505050;
  }
}

.success-message {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
    color: #ffffff;
    font-size: 32px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }

  &__link-container {
    padding: 16px 16px 46px;
    background: #e0e0e0;

    span {
      word-break: break-all;
    }
    button {
      align-self: flex-end;
      background: transparent;
      border: none;
      cursor: pointer;
    }
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
  }

  &__text {
    font-size: 14px;
    color: #505050;
    margin: 0;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: left;
  }

  &__item {
    font-size: 14px;
    line-height: 1.6;
    color: #262626;
    margin-bottom: 12px;
    display: flex;
    align-items: flex-start;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__bullet {
    color: #009ee3;
    font-weight: 600;
    margin-right: 8px;
    flex-shrink: 0;
  }
}
</style>
